// src/components/FundingFinder.jsx
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const C = {
  navy: '#122326', slate: '#213A3E', gold: '#BF6847',
  cream: '#F0EFEB', warm: '#F7F6F3', sand: '#E2E0DB', white: '#FFFFFF',
  green: '#2E7D32', greenBg: '#e8f5e9', greenBorder: '#a5d6a7',
  amber: '#b45309', amberBg: '#fef3c7', amberBorder: '#fcd34d',
  red: '#991b1b', redBg: '#fde8e8', redBorder: '#fca5a5',
  blue: '#1565C0', blueBg: '#e3f2fd',
};
const SANS  = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const SERIF = "'Georgia', serif";

// ── CEDS SCORING (8-criterion, 100-point matrix) ─────────────────────────────
// Formula: Total = Σ (raw_score / 4) × weight   where raw_score ∈ {1,2,3,4}

const CEDS_CRITERIA = [
  { id: 'matchingFunds',    label: 'Matching Funds Secured',       weight: 20 },
  { id: 'shovelReady',      label: 'Shovel-Ready Status',          weight: 15 },
  { id: 'communitySupport', label: 'Community / Political Support', weight: 15 },
  { id: 'cedsAlignment',    label: 'CEDS Priority Alignment',      weight: 15 },
  { id: 'jobsCreated',      label: 'Jobs Retained or Created',     weight: 15 },
  { id: 'peopleServed',     label: 'People / Households Served',   weight: 10 },
  { id: 'startTimeline',    label: 'Timeline to Completion',       weight: 5  },
  { id: 'paddCapacity',     label: 'PADD Capacity to Support',     weight: 5  },
];

// Option text → raw score (1–4). Options written to exactly match the rubric language.
const SCORE_MAP = {
  matchingFunds: {
    'No matching funds identified — project relies entirely on grant': 1,
    'Match identified but not yet secured — verbal or letter of interest only': 2,
    'Match partially secured: 25–74% committed in writing or via signed MOU': 3,
    'Full match secured: ≥75% in hand or formally committed via signed agreement': 4,
  },
  shovelReady: {
    'Concept only — no engineering, permits, or environmental review initiated': 1,
    'Preliminary engineering or feasibility study complete; permitting not started': 2,
    'Engineering ≥50% complete; permits applied for or environmental review underway': 3,
    'Construction-ready — engineering 100%, all permits in hand, environmental clearance received': 4,
  },
  communitySupport: {
    'Verbal interest only from one stakeholder; no formal documentation': 1,
    'Support from project champion or elected official; no governing body resolution yet': 2,
    'Multiple stakeholders committed; at least one governing body resolution or formal letter': 3,
    'Governing body resolution passed; multiple letters of support; no known opposition': 4,
  },
  cedsAlignment: {
    'Tangential — addresses a low-priority issue or falls outside CEDS goal areas': 1,
    'Moderate — related to a CEDS goal area but does not directly advance a stated strategy': 2,
    'Strong — directly advances at least one CEDS strategy under a high-priority goal': 3,
    'Exceptional — centerpiece strategy advancing multiple CEDS goals with regional impact': 4,
  },
  jobsCreated: {
    'No direct job impact expected (quality-of-life or administrative project)': 1,
    '1–5 direct, permanent full-time equivalent jobs': 2,
    '6–20 jobs expected with documentation (employer letter or economic impact estimate)': 3,
    '21+ jobs OR critical infrastructure enabling significant future job creation (documented)': 4,
  },
  peopleServed: {
    'Fewer than 50 people or a single business/property': 1,
    '50–249 people or one community with limited regional spillover': 2,
    '250–999 people across one or more communities; regional benefit present': 3,
    '1,000+ people OR direct regional impact across multiple counties': 4,
  },
  startTimeline: {
    'Timeline undefined or project expected to take more than 5 years': 1,
    '3–5 years; reasonable timeline but lacks defined milestones': 2,
    '1–3 years with defined milestones and a committed timeline': 3,
    'Completable within 12 months with a detailed schedule and committed partners': 4,
  },
  paddCapacity: {
    'Would require PADD to lead with no identified partners; exceeds current staff capacity': 1,
    'PADD can support but the project requires significant new expertise or capacity': 2,
    'Manageable within existing PADD workload; relevant experience and partner relationships in place': 3,
    'PADD is the natural lead or strong co-lead — excellent fit with current staff expertise and active partners': 4,
  },
};

function calcCEDS(answers) {
  let total = 0;
  const breakdown = {};
  for (const c of CEDS_CRITERIA) {
    const raw   = SCORE_MAP[c.id]?.[answers[c.id]] ?? 0;
    const pts   = raw > 0 ? (raw / 4) * c.weight : 0;
    breakdown[c.id] = { raw, pts: Math.round(pts * 10) / 10, max: c.weight, label: c.label };
    total += pts;
  }
  return { total: Math.round(total), breakdown };
}

function cedsTier(score) {
  if (score >= 70) return { label: 'Featured Project',  bg: C.greenBg,  color: C.green,  border: C.greenBorder, desc: 'Actively promoted. PADD provides grant writing, technical assistance, and priority staff attention.' };
  if (score >= 40) return { label: 'Included Project',  bg: C.amberBg,  color: C.amber,  border: C.amberBorder, desc: 'Listed in CEDS. PADD provides general guidance when staff capacity allows.' };
  return               { label: 'Listed Only',          bg: C.redBg,    color: C.red,    border: C.redBorder,   desc: 'Acknowledged per EDA requirements. Community encouraged to strengthen and resubmit.' };
}

// ── GRANT MATCHING ────────────────────────────────────────────────────────────

const COST_TIERS = ['Under $50,000', '$50,000–$250,000', '$250,000–$1 million', '$1M–$5M', '$5M+'];
const POP_VALUES = { 'Under 500': 499, '500–1,000': 1000, '1,000–5,000': 5000, '5,000–10,000': 10000, '10,000+': 999999 };

const GRANTS = [
  { id: 'cdbg-infra',    name: 'CDBG — Infrastructure & Public Facilities', funder: 'Nebraska DED / HUD',                    desc: 'Flexible federal block grant for water, sewer, streets, and community facilities. The primary funding source for most Panhandle municipalities.',              size: '$100,000 – $750,000',           matchReq: 'Not required',                    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',                                                                categories: ['Infrastructure (water, sewer, roads, broadband)', 'Community Facilities (community centers, libraries, childcare)', 'Downtown / Revitalization'], orgs: ['City / Village', 'County'],                                    maxPop: null,  ruralReq: false, matchReq2: false, minReadiness: 1, minCostTier: 1 },
  { id: 'cdbg-housing',  name: 'CDBG — Housing Rehabilitation',             funder: 'Nebraska DED / HUD',                    desc: 'CDBG funding for owner-occupied housing rehabilitation programs serving low-to-moderate income households.',                                            size: '$300,000 – $500,000',           matchReq: 'Not required',                    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',                                                                categories: ['Housing (development, rehab, workforce housing)'],                                                                                                  orgs: ['City / Village', 'County'],                                    maxPop: null,  ruralReq: false, matchReq2: false, minReadiness: 1, minCostTier: 1 },
  { id: 'cdbg-ed',       name: 'CDBG — Economic Development',               funder: 'Nebraska DED / HUD',                    desc: 'CDBG funds tied to business expansion or retention creating jobs for LMI workers. Requires private leverage from the business.',                         size: 'Varies by project',             matchReq: 'Private leverage required',       url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',                                                                categories: ['Business / Economic Development'],                                                                                                                  orgs: ['City / Village', 'County'],                                    maxPop: null,  ruralReq: false, matchReq2: true,  minReadiness: 2, minCostTier: 1 },
  { id: 'cdbg-planning', name: 'CDBG — Planning Grant',                     funder: 'Nebraska DED / HUD',                    desc: 'Smaller CDBG awards for needs assessments, feasibility studies, and community planning. Low readiness bar.',                                              size: 'Up to $50,000',                 matchReq: 'Not required',                    url: 'https://opportunity.nebraska.gov/programs/community/cdbg/',                                                                categories: ['Planning / Studies'],                                                                                                                               orgs: ['City / Village', 'County'],                                    maxPop: null,  ruralReq: false, matchReq2: false, minReadiness: 0, minCostTier: 0 },
  { id: 'usda-cf',       name: 'USDA Community Facilities Grant',            funder: 'USDA Rural Development',                desc: 'Grants and loans for essential community facilities in rural areas — fire stations, libraries, healthcare, childcare, and more.',                         size: '$10,000 – $500,000 (grant)',    matchReq: 'Often required (varies)',         url: 'https://www.rd.usda.gov/programs-services/community-facilities/community-facilities-direct-loan-grant-program',            categories: ['Community Facilities (community centers, libraries, childcare)', 'Infrastructure (water, sewer, roads, broadband)'],                               orgs: ['City / Village', 'County', 'Nonprofit', 'Tribe / Tribal Organization'], maxPop: 20000, ruralReq: true,  matchReq2: false, minReadiness: 1, minCostTier: 0 },
  { id: 'usda-water',    name: 'USDA Water & Waste Disposal Grant',          funder: 'USDA Rural Development',                desc: 'Grants covering up to 75% of project costs for drinking water, sanitary sewer, solid waste, and stormwater in rural communities.',                       size: 'Up to 75% of project cost',    matchReq: '25%+ match required',             url: 'https://www.rd.usda.gov/programs-services/water-environmental-programs/water-waste-disposal-loan-grant-program',           categories: ['Infrastructure (water, sewer, roads, broadband)'],                                                                                                  orgs: ['City / Village', 'County'],                                    maxPop: 10000, ruralReq: true,  matchReq2: true,  minReadiness: 1, minCostTier: 1 },
  { id: 'eda-pw',        name: 'EDA Public Works & Economic Development',    funder: 'U.S. Economic Development Administration', desc: 'Federal investment in public infrastructure and facilities that create or retain jobs. For larger projects with clear regional economic impact.',       size: '$1M – $10M+',                  matchReq: '50% match typically required',    url: 'https://www.eda.gov/funding/programs/public-works',                                                                        categories: ['Infrastructure (water, sewer, roads, broadband)', 'Community Facilities (community centers, libraries, childcare)', 'Business / Economic Development'], orgs: ['City / Village', 'County', 'Nonprofit', 'Tribe / Tribal Organization'], maxPop: null,  ruralReq: false, matchReq2: true,  minReadiness: 2, minCostTier: 3 },
  { id: 'usda-bi',       name: 'USDA Business & Industry Loan Guarantee',   funder: 'USDA Rural Development',                desc: 'Loan guarantees up to 80% for rural businesses. Supports job creation, business expansion, and rural economic development.',                             size: 'Loans up to $25M',             matchReq: 'Equity contribution required',    url: 'https://www.rd.usda.gov/programs-services/business-programs/business-industry-loan-guarantees',                           categories: ['Business / Economic Development'],                                                                                                                  orgs: ['Small Business', 'Nonprofit', 'City / Village', 'County'],    maxPop: 50000, ruralReq: true,  matchReq2: true,  minReadiness: 2, minCostTier: 1 },
  { id: 'ne-ahtf',       name: 'Nebraska Affordable Housing Trust Fund',    funder: 'Nebraska Investment Finance Authority', desc: 'State funding for affordable housing development, rehabilitation, and homeownership programs with documented housing need.',                             size: 'Varies by project type',       matchReq: 'Leverage required',               url: 'https://www.nifa.org/affordable-housing-trust-fund/',                                                                      categories: ['Housing (development, rehab, workforce housing)'],                                                                                                  orgs: ['Nonprofit', 'City / Village', 'County'],                       maxPop: null,  ruralReq: false, matchReq2: true,  minReadiness: 1, minCostTier: 1 },
  { id: 'tourism',       name: 'Nebraska Tourism Development Grant',        funder: 'Nebraska Tourism Commission',           desc: 'Grants to develop tourism infrastructure, improve visitor experiences, and market Nebraska destinations.',                                              size: 'Up to $50,000',                matchReq: '50% match required',              url: 'https://tourism.nebraska.gov/grants/',                                                                                     categories: ['Tourism / Recreation'],                                                                                                                             orgs: ['City / Village', 'County', 'Nonprofit'],                       maxPop: null,  ruralReq: false, matchReq2: true,  minReadiness: 1, minCostTier: 0 },
  { id: 'home',          name: 'HOME Investment Partnerships Program',      funder: 'Nebraska DED / HUD',                   desc: 'Federal housing program for affordable rental development, homebuyer assistance, and owner-occupied rehab for low-income households.',                  size: 'Varies',                       matchReq: '25% match required',              url: 'https://opportunity.nebraska.gov/programs/community/',                                                                     categories: ['Housing (development, rehab, workforce housing)'],                                                                                                  orgs: ['Nonprofit', 'City / Village', 'County'],                       maxPop: null,  ruralReq: false, matchReq2: true,  minReadiness: 1, minCostTier: 1 },
];

const SHOVEL_READINESS = {
  'Concept only — no engineering, permits, or environmental review initiated': 0,
  'Preliminary engineering or feasibility study complete; permitting not started': 1,
  'Engineering ≥50% complete; permits applied for or environmental review underway': 2,
  'Construction-ready — engineering 100%, all permits in hand, environmental clearance received': 3,
};

function matchGrants(answers) {
  const pop          = POP_VALUES[answers.population] ?? null;
  const isRural      = answers.isRural === 'Yes';
  const matchScore   = SCORE_MAP.matchingFunds[answers.matchingFunds] ?? 1;
  const hasMatch     = matchScore >= 3;
  const matchSecured = matchScore === 4;
  const readiness    = SHOVEL_READINESS[answers.shovelReady] ?? 0;
  const costTierIdx  = COST_TIERS.indexOf(answers.totalCost);

  return GRANTS.map(g => {
    let score = 0;
    const flags = [];

    if (g.orgs.includes(answers.orgType))              { score += 3; } else { score -= 3; flags.push('Org type may not qualify'); }
    if (g.categories.includes(answers.projectCategory)) { score += 3; } else { score -= 3; flags.push('Project type may not match'); }

    if (g.ruralReq && !isRural)               { score -= 4; flags.push('Rural area required'); }
    else if (g.ruralReq && isRural)            { score += 1; }

    if (g.maxPop && pop && pop > g.maxPop)    { score -= 3; flags.push(`Population may exceed limit`); }
    else if (g.maxPop && pop && pop <= g.maxPop) { score += 1; }

    if (g.matchReq2 && !hasMatch)             { score -= 3; flags.push('Matching funds required'); }
    else if (g.matchReq2 && matchSecured)      { score += 2; }
    else if (g.matchReq2 && hasMatch)          { score += 1; }

    if (readiness < g.minReadiness)           { score -= 2; flags.push('Project may need more development'); }
    else                                       { score += Math.min(readiness - g.minReadiness + 1, 2); }

    if (g.id === 'eda-pw' && costTierIdx < 3) { score -= 2; flags.push('Best suited for $1M+ projects'); }
    if (costTierIdx >= 0 && costTierIdx < g.minCostTier) { score -= 1; }

    const priority = score >= 6 ? 'high' : score >= 3 ? 'medium' : score >= 0 ? 'low' : null;
    return { ...g, score, priority, flags };
  })
  .filter(g => g.priority !== null)
  .sort((a, b) => b.score - a.score);
}

// ── SECTIONS / QUESTIONS ─────────────────────────────────────────────────────

const SECTIONS = [
  {
    id: 'overview', title: 'Project Overview',
    questions: [
      { id: 'projectDescription', type: 'text',     required: true,  label: 'What are you hoping to fund?',         hint: 'A sentence or two — staff uses this for initial review.',                                                           placeholder: 'Example: Replace 3 miles of aging water mains to address EPA violations and reduce main breaks.' },
      { id: 'projectCategory',    type: 'radio',    required: true,  label: 'Which category best fits your project?', options: ['Infrastructure (water, sewer, roads, broadband)', 'Housing (development, rehab, workforce housing)', 'Business / Economic Development', 'Community Facilities (community centers, libraries, childcare)', 'Downtown / Revitalization', 'Tourism / Recreation', 'Planning / Studies', 'Other'] },
      { id: 'projectLocation',    type: 'text',     required: true,  label: 'Where will this project take place?',  hint: 'City/Town and County',                                                                                             placeholder: 'Example: Chadron, Dawes County' },
      {
        id: 'shovelReady', type: 'radio', required: true,
        label: 'How far along is your project? (C2 — Shovel-Ready Status)',
        hint: 'This is one of the 8 criteria PADD uses to score projects for the CEDS. Be honest — vague answers score lower.',
        options: [
          'Concept only — no engineering, permits, or environmental review initiated',
          'Preliminary engineering or feasibility study complete; permitting not started',
          'Engineering ≥50% complete; permits applied for or environmental review underway',
          'Construction-ready — engineering 100%, all permits in hand, environmental clearance received',
        ],
      },
    ],
  },
  {
    id: 'applicant', title: 'About Your Organization',
    questions: [
      { id: 'orgType', type: 'radio',  required: true,  label: 'What type of organization are you?',       options: ['City / Village', 'County', 'Nonprofit', 'Small Business', 'Tribe / Tribal Organization', 'School / Education Entity', 'Other'] },
      { id: 'orgName', type: 'text',   required: true,  label: 'Organization name',                        placeholder: 'Village of Harrison' },
      { id: 'population', type: 'select', required: true, label: 'Population of your community', hint: 'Select "Not applicable" if not a municipality.', options: ['Under 500', '500–1,000', '1,000–5,000', '5,000–10,000', '10,000+', 'Not applicable'], skip: a => a.orgType === 'Small Business' },
      { id: 'isRural', type: 'radio',  required: true,  label: 'Are you located in a rural area?',         hint: 'Many federal programs define rural as areas outside urbanized centers.',  options: ['Yes', 'No', 'Not sure'] },
    ],
  },
  {
    id: 'details', title: 'Project Details',
    questions: [
      { id: 'totalCost',    type: 'radio',    required: true,  label: 'Estimated total project cost',                   options: ['Under $50,000', '$50,000–$250,000', '$250,000–$1 million', '$1M–$5M', '$5M+'] },
      { id: 'fundingNeeded', type: 'text',    required: true,  label: 'How much grant funding are you seeking?',         hint: 'A range is fine.',  placeholder: 'Example: $400,000–$600,000' },
      { id: 'fundingUses',  type: 'checkbox', required: true,  label: 'What will the funding be used for?',             options: ['Planning / studies', 'Land acquisition', 'Design / engineering', 'Construction / implementation', 'Equipment', 'Operations / programming'] },
      {
        id: 'startTimeline', type: 'radio', required: true,
        label: 'Estimated timeline to completion (C7)',
        hint: 'Projects with defined milestones and near-term timelines score higher.',
        options: [
          'Timeline undefined or project expected to take more than 5 years',
          '3–5 years; reasonable timeline but lacks defined milestones',
          '1–3 years with defined milestones and a committed timeline',
          'Completable within 12 months with a detailed schedule and committed partners',
        ],
      },
    ],
  },
  {
    id: 'readiness', title: 'Funding Readiness',
    questions: [
      {
        id: 'matchingFunds', type: 'radio', required: true,
        label: 'Status of your local match (C1 — Matching Funds, worth 20 pts)',
        hint: 'The single highest-weighted criterion. Secured match dramatically improves your score and competitiveness.',
        options: [
          'No matching funds identified — project relies entirely on grant',
          'Match identified but not yet secured — verbal or letter of interest only',
          'Match partially secured: 25–74% committed in writing or via signed MOU',
          'Full match secured: ≥75% in hand or formally committed via signed agreement',
        ],
      },
      { id: 'matchingAmount',  type: 'text',  required: false, label: 'Describe your match sources',  hint: 'List dollar amounts and funding sources.',  placeholder: 'Example: $150,000 ARPA, $50,000 city reserves — total $200,000 committed via resolution',  skip: a => a.matchingFunds === 'No matching funds identified — project relies entirely on grant' },
      { id: 'grantExperience', type: 'radio', required: true,  label: 'Has your organization received grants before?',  options: ['Yes, multiple grants', 'Yes, once or twice', 'No'] },
      {
        id: 'paddCapacity', type: 'radio', required: true,
        label: 'PADD\'s capacity to support this project (C8)',
        hint: 'Be realistic. Projects PADD can actively champion move faster.',
        options: [
          'Would require PADD to lead with no identified partners; exceeds current staff capacity',
          'PADD can support but the project requires significant new expertise or capacity',
          'Manageable within existing PADD workload; relevant experience and partner relationships in place',
          'PADD is the natural lead or strong co-lead — excellent fit with current staff expertise and active partners',
        ],
      },
    ],
  },
  {
    id: 'alignment', title: 'Community Support & CEDS Alignment',
    questions: [
      {
        id: 'communitySupport', type: 'radio', required: true,
        label: 'Level of community and political support (C3)',
        hint: 'Governing body resolutions, letters of support, and broad stakeholder buy-in are key signals.',
        options: [
          'Verbal interest only from one stakeholder; no formal documentation',
          'Support from project champion or elected official; no governing body resolution yet',
          'Multiple stakeholders committed; at least one governing body resolution or formal letter',
          'Governing body resolution passed; multiple letters of support; no known opposition',
        ],
      },
      {
        id: 'cedsAlignment', type: 'radio', required: true,
        label: 'Alignment with PADD\'s CEDS regional strategy (C4)',
        hint: 'CEDS priorities include infrastructure resilience, housing, workforce development, and business growth across the Panhandle.',
        options: [
          'Tangential — addresses a low-priority issue or falls outside CEDS goal areas',
          'Moderate — related to a CEDS goal area but does not directly advance a stated strategy',
          'Strong — directly advances at least one CEDS strategy under a high-priority goal',
          'Exceptional — centerpiece strategy advancing multiple CEDS goals with regional impact',
        ],
      },
      {
        id: 'jobsCreated', type: 'radio', required: true,
        label: 'Expected jobs created or retained (C5)',
        hint: 'Include direct, permanent full-time equivalent positions. Infrastructure enabling future jobs also qualifies.',
        options: [
          'No direct job impact expected (quality-of-life or administrative project)',
          '1–5 direct, permanent full-time equivalent jobs',
          '6–20 jobs expected with documentation (employer letter or economic impact estimate)',
          '21+ jobs OR critical infrastructure enabling significant future job creation (documented)',
        ],
      },
      {
        id: 'peopleServed', type: 'radio', required: true,
        label: 'Number of people or households this project will directly benefit (C6)',
        options: [
          'Fewer than 50 people or a single business/property',
          '50–249 people or one community with limited regional spillover',
          '250–999 people across one or more communities; regional benefit present',
          '1,000+ people OR direct regional impact across multiple counties',
        ],
      },
    ],
  },
  {
    id: 'contact', title: 'Context & Contact',
    questions: [
      { id: 'whyNow',         type: 'textarea', required: false, label: 'Why is this project important right now?', hint: 'Optional — urgency, prior funding attempts, regulatory drivers, or regional significance. 2–3 sentences.',  placeholder: 'Our water system has had two main breaks this year and we received an EPA notice...' },
      { id: 'estimatedImpact', type: 'text',    required: false, label: 'Estimated impact (if known)',              hint: 'Examples: housing units, road miles, business count, people served — leave blank if unknown.',              placeholder: 'Example: 800 residents served, 2.5 miles of water main replaced' },
      { id: '_contact',       type: 'contact',  required: true,  label: 'Your name and contact information',        hint: 'Your information is never shared or sold.' },
      { id: 'helpNeeded',     type: 'radio',    required: true,  label: 'Would you like help applying for funding?', options: ['Yes — help with grant writing', 'Yes — help with grant administration', 'Maybe — tell me more about PADD services', 'No, just exploring opportunities'] },
      { id: 'applyTimeline',  type: 'radio',    required: true,  label: 'How soon are you looking to apply?',       options: ['ASAP (within 3 months)', '3–6 months', '6+ months', 'Just exploring'] },
      { id: 'anythingElse',   type: 'textarea', required: false, label: 'Anything else PADD should know?',          placeholder: 'Partnerships, constraints, prior attempts, or details that might help us prioritize your project.' },
    ],
  },
];

const INITIAL_ANSWERS = {
  projectDescription: '', projectCategory: '', projectLocation: '', shovelReady: '',
  orgType: '', orgName: '', population: '', isRural: '',
  totalCost: '', fundingNeeded: '', fundingUses: [], startTimeline: '',
  matchingFunds: '', matchingAmount: '', grantExperience: '', paddCapacity: '',
  communitySupport: '', cedsAlignment: '', jobsCreated: '', peopleServed: '',
  whyNow: '', estimatedImpact: '', contactName: '', contactEmail: '', contactPhone: '',
  helpNeeded: '', applyTimeline: '', anythingElse: '',
};

function validate(sectionIdx, answers) {
  if (typeof sectionIdx !== 'number') return null;
  for (const q of SECTIONS[sectionIdx].questions) {
    if (q.skip && q.skip(answers)) continue;
    if (!q.required) continue;
    if (q.type === 'checkbox') {
      if (!answers[q.id]?.length) return `Please select at least one option for "${q.label.split(' (')[0]}".`;
    } else if (q.type === 'contact') {
      if (!answers.contactName?.trim()) return 'Please enter your name.';
      if (!answers.contactEmail?.trim()) return 'Please enter your email address.';
    } else {
      if (!answers[q.id]?.trim?.()) return `Please answer: "${q.label.split(' (')[0].substring(0, 60)}".`;
    }
  }
  return null;
}

// ── QUESTION RENDERER ─────────────────────────────────────────────────────────

function QuestionBlock({ q, answers, setAnswer, toggleCheck }) {
  const val = answers[q.id];
  const inputBase = { width: '100%', padding: '10px 14px', borderRadius: 8, border: `1.5px solid ${C.sand}`, fontSize: 14, fontFamily: SANS, color: C.navy, boxSizing: 'border-box', outline: 'none', background: C.white };
  const onFocus = e => (e.target.style.borderColor = C.gold);
  const onBlur  = e => (e.target.style.borderColor = C.sand);

  return (
    <div style={{ marginBottom: 28 }}>
      <label style={{ display: 'block', fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: q.hint ? 4 : 10 }}>
        {q.label} {q.required && <span style={{ color: C.gold }}>*</span>}
      </label>
      {q.hint && <p style={{ fontSize: 13, color: C.slate, margin: '0 0 10px', lineHeight: 1.5 }}>{q.hint}</p>}

      {q.type === 'text' && <input type="text" value={val || ''} onChange={e => setAnswer(q.id, e.target.value)} placeholder={q.placeholder} style={inputBase} onFocus={onFocus} onBlur={onBlur} />}
      {q.type === 'textarea' && <textarea value={val || ''} onChange={e => setAnswer(q.id, e.target.value)} placeholder={q.placeholder} rows={3} style={{ ...inputBase, resize: 'vertical' }} onFocus={onFocus} onBlur={onBlur} />}
      {q.type === 'select' && (
        <select value={val || ''} onChange={e => setAnswer(q.id, e.target.value)} style={{ ...inputBase, cursor: 'pointer' }}>
          <option value="">Select one…</option>
          {q.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      )}
      {q.type === 'radio' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {q.options.map((o, idx) => {
            const sel = val === o;
            const scoreHint = SCORE_MAP[q.id] ? `${SCORE_MAP[q.id][o]}/4` : null;
            return (
              <button key={o} type="button" onClick={() => setAnswer(q.id, o)} style={{ padding: '10px 16px', borderRadius: 8, fontSize: 14, cursor: 'pointer', border: `1.5px solid ${sel ? C.gold : C.sand}`, background: sel ? C.gold : C.white, color: sel ? C.white : C.slate, fontWeight: sel ? 700 : 400, textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, transition: 'all 0.15s' }}>
                <span>{o}</span>
                {scoreHint && <span style={{ fontSize: 11, fontWeight: 700, opacity: sel ? 0.85 : 0.5, flexShrink: 0 }}>{scoreHint}</span>}
              </button>
            );
          })}
        </div>
      )}
      {q.type === 'checkbox' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {q.options.map(o => {
            const chk = (val || []).includes(o);
            return (
              <button key={o} type="button" onClick={() => toggleCheck(q.id, o)} style={{ padding: '10px 16px', borderRadius: 8, fontSize: 14, cursor: 'pointer', border: `1.5px solid ${chk ? C.gold : C.sand}`, background: chk ? '#fdf3ef' : C.white, color: chk ? C.gold : C.slate, fontWeight: chk ? 700 : 400, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.15s' }}>
                <span style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${chk ? C.gold : C.sand}`, background: chk ? C.gold : C.white, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{chk && <span style={{ color: C.white, fontSize: 12 }}>✓</span>}</span>
                {o}
              </button>
            );
          })}
        </div>
      )}
      {q.type === 'contact' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input type="text"  value={answers.contactName  || ''} onChange={e => setAnswer('contactName',  e.target.value)} placeholder="Full name *"                   style={inputBase} onFocus={onFocus} onBlur={onBlur} />
          <input type="email" value={answers.contactEmail || ''} onChange={e => setAnswer('contactEmail', e.target.value)} placeholder="Email address *"               style={inputBase} onFocus={onFocus} onBlur={onBlur} />
          <input type="tel"   value={answers.contactPhone || ''} onChange={e => setAnswer('contactPhone', e.target.value)} placeholder="Phone number (optional)"       style={inputBase} onFocus={onFocus} onBlur={onBlur} />
        </div>
      )}
    </div>
  );
}

// ── RESULTS PAGE ──────────────────────────────────────────────────────────────

const PRIORITY_CONFIG = {
  high:   { label: 'High Priority',   bg: C.greenBg, color: C.green, border: C.greenBorder },
  medium: { label: 'Good Match',      bg: C.amberBg, color: C.amber, border: C.amberBorder },
  low:    { label: 'Worth Exploring', bg: C.blueBg,  color: C.blue,  border: '#90caf9'     },
};

function ResultsPage({ answers, ceds, matches, onBack }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const tier  = cedsTier(ceds.total);
  const wantsHelp = answers.helpNeeded && !answers.helpNeeded.startsWith('No');
  const grouped = { high: matches.filter(m => m.priority === 'high'), medium: matches.filter(m => m.priority === 'medium'), low: matches.filter(m => m.priority === 'low') };

  // Lowest-scoring criteria — for improvement suggestions
  const sorted = CEDS_CRITERIA
    .map(c => ({ ...c, ...ceds.breakdown[c.id] }))
    .sort((a, b) => (a.pts / a.max) - (b.pts / b.max));
  const lowScorers = sorted.filter(c => c.raw > 0 && c.raw < 3).slice(0, 3);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: C.warm, fontFamily: SANS }}>
      <nav style={{ backgroundColor: C.navy, padding: '14px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 700, color: C.cream }}>PADD CDBG Hub</span>
        <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: C.cream, padding: '6px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>← Edit Answers</button>
      </nav>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px 80px' }}>

        {/* ── CEDS Score Card ── */}
        <div style={{ background: C.navy, borderRadius: 16, padding: '32px 36px', marginBottom: 24, color: C.cream }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#DBDDAC', marginBottom: 8 }}>
            CEDS Project Assessment · 2025–2030
          </div>
          <h1 style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 700, margin: '0 0 4px', lineHeight: 1.2 }}>{answers.orgName || 'Your Project'}</h1>
          <div style={{ fontSize: 13, color: '#b0bec5', marginBottom: 20 }}>
            {[answers.projectCategory?.split(' (')[0], answers.projectLocation].filter(Boolean).join(' · ')}
          </div>

          {/* Score display */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 52, fontWeight: 900, color: tier.color, lineHeight: 1 }}>{ceds.total}</div>
              <div style={{ fontSize: 12, color: '#78909c' }}>out of 100</div>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 99, height: 12, marginBottom: 10, overflow: 'hidden' }}>
                <div style={{ width: `${ceds.total}%`, height: '100%', background: tier.color, borderRadius: 99, transition: 'width 0.8s ease' }} />
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 20, background: tier.bg, border: `1px solid ${tier.border}` }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: tier.color }}>{tier.label}</span>
              </div>
            </div>
          </div>
          <p style={{ fontSize: 13, color: '#b0bec5', margin: 0, lineHeight: 1.6 }}>{tier.desc}</p>
        </div>

        {/* ── Score Breakdown ── */}
        <div style={{ background: C.white, border: `1px solid ${C.sand}`, borderRadius: 12, marginBottom: 24, overflow: 'hidden' }}>
          <button
            onClick={() => setShowBreakdown(v => !v)}
            style={{ width: '100%', padding: '16px 22px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>Score Breakdown — 8 Criteria</span>
            <span style={{ fontSize: 18, color: C.gold }}>{showBreakdown ? '−' : '+'}</span>
          </button>

          {showBreakdown && (
            <div style={{ borderTop: `1px solid ${C.sand}` }}>
              {CEDS_CRITERIA.map(c => {
                const bd  = ceds.breakdown[c.id];
                const pct = bd.raw > 0 ? (bd.raw / 4) * 100 : 0;
                const col = pct >= 75 ? C.green : pct >= 50 ? C.amber : C.red;
                const scoreLabels = ['—', 'Minimal', 'Partial', 'Substantial', 'Excellent'];
                return (
                  <div key={c.id} style={{ padding: '12px 22px', borderBottom: `1px solid ${C.sand}`, display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 4 }}>{c.label}</div>
                      <div style={{ background: C.sand, borderRadius: 99, height: 6, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: col, borderRadius: 99 }} />
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: col }}>{bd.pts}/{c.weight} pts</div>
                      <div style={{ fontSize: 11, color: C.slate }}>{scoreLabels[bd.raw] || '—'}</div>
                    </div>
                  </div>
                );
              })}
              <div style={{ padding: '12px 22px', background: C.cream, display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: C.navy }}>
                <span>Total Score</span><span>{ceds.total} / 100</span>
              </div>
            </div>
          )}
        </div>

        {/* ── Improvement Tips ── */}
        {ceds.total < 70 && lowScorers.length > 0 && (
          <div style={{ background: C.amberBg, border: `1px solid ${C.amberBorder}`, borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.amber, marginBottom: 10 }}>
              To reach Featured status (70+ points), focus on these areas:
            </div>
            {lowScorers.map(c => (
              <div key={c.id} style={{ fontSize: 13, color: C.amber, marginBottom: 4, display: 'flex', gap: 8 }}>
                <span>→</span>
                <span><strong>{c.label}:</strong> currently {c.pts}/{c.max} pts — improving from "{CEDS_CRITERIA.find(x=>x.id===c.id) && Object.entries(SCORE_MAP[c.id] || {}).find(([k]) => k === answers[c.id])?.[0]?.substring(0, 60)}…" would add up to {c.max - c.pts} pts</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Grant Matches ── */}
        <h2 style={{ fontFamily: SERIF, fontSize: 20, fontWeight: 700, color: C.navy, margin: '0 0 4px' }}>
          {matches.length} Funding {matches.length === 1 ? 'Program' : 'Programs'} to Pursue
        </h2>
        <p style={{ fontSize: 13, color: C.slate, margin: '0 0 20px' }}>Based on your project type, organization, and eligibility profile.</p>

        {matches.length === 0 && (
          <div style={{ background: C.white, border: `1px solid ${C.sand}`, borderRadius: 12, padding: '32px 24px', textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
            <p style={{ fontSize: 14, color: C.slate, lineHeight: 1.6 }}>No strong matches found based on current answers. Contact PADD — we often know about emerging programs not yet listed here.</p>
          </div>
        )}

        {['high', 'medium', 'low'].map(tier2 => {
          const group = grouped[tier2];
          if (!group.length) return null;
          const cfg = PRIORITY_CONFIG[tier2];
          return (
            <div key={tier2} style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>{cfg.label}</span>
                <span style={{ fontSize: 12, color: C.slate }}>{group.length} {group.length === 1 ? 'program' : 'programs'}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {group.map(g => (
                  <div key={g.id} style={{ background: C.white, border: `1px solid ${C.sand}`, borderRadius: 12, padding: '18px 22px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 2 }}>{g.name}</div>
                        <div style={{ fontSize: 12, color: C.slate }}>{g.funder}</div>
                      </div>
                      <a href={g.url} target="_blank" rel="noreferrer" style={{ flexShrink: 0, fontSize: 12, color: C.gold, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>Learn More ↗</a>
                    </div>
                    <p style={{ fontSize: 13, color: C.slate, lineHeight: 1.6, margin: '8px 0' }}>{g.desc}</p>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: C.slate }}>
                      <span>💰 <strong>Size:</strong> {g.size}</span>
                      <span>🤝 <strong>Match:</strong> {g.matchReq}</span>
                    </div>
                    {g.flags.length > 0 && <div style={{ marginTop: 10, padding: '7px 12px', background: C.cream, borderRadius: 6, fontSize: 12, color: C.slate }}>⚠️ {g.flags.join(' · ')}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* ── CTA ── */}
        <div style={{ background: C.navy, borderRadius: 16, padding: '32px 36px', marginTop: 16, textAlign: 'center' }}>
          <h3 style={{ fontFamily: SERIF, fontSize: 20, fontWeight: 700, color: C.cream, margin: '0 0 10px' }}>
            {ceds.total >= 70 ? 'Your project qualifies for Featured status.' : wantsHelp ? 'Ready to strengthen your project?' : 'Questions about your results?'}
          </h3>
          <p style={{ fontSize: 14, color: '#b0bec5', margin: '0 0 20px', lineHeight: 1.6 }}>
            {ceds.total >= 70
              ? 'Submit your project to the PADD CEDS to receive active grant-writing support and priority staff attention. Contact Kyle Keller to get started.'
              : `Your project scored ${ceds.total}/100. PADD can help you close the gap — matching funds and shovel-ready status have the highest impact on your score.`}
          </p>
          <a
            href={`mailto:kylek@nepadd.com?subject=CEDS Project Inquiry — ${answers.orgName || 'Project'}&body=I completed the PADD Funding Finder. My project: ${answers.projectDescription || ''}%0D%0A%0D%0ACEDS Score: ${ceds.total}/100 (${tier.label})%0D%0AOrganization: ${answers.orgName || ''}%0D%0ALocation: ${answers.projectLocation || ''}`}
            style={{ display: 'inline-block', padding: '12px 28px', borderRadius: 10, background: C.gold, color: C.white, fontWeight: 700, fontSize: 15, textDecoration: 'none' }}
          >
            Contact Kyle Keller →
          </a>
          <div style={{ fontSize: 12, color: '#78909c', marginTop: 10 }}>kylek@nepadd.com · 308-632-4957</div>
        </div>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function FundingFinder() {
  const navigate = useNavigate();
  const [step, setStep]       = useState('intro');
  const [answers, setAnswers] = useState(INITIAL_ANSWERS);
  const [error, setError]     = useState(null);

  const ceds    = useMemo(() => step === 'results' ? calcCEDS(answers)    : { total: 0, breakdown: {} }, [step, answers]);
  const matches = useMemo(() => step === 'results' ? matchGrants(answers) : [],                          [step, answers]);

  function setAnswer(id, val) { setAnswers(p => ({ ...p, [id]: val })); }
  function toggleCheck(id, opt) { setAnswers(p => { const c = p[id] || []; return { ...p, [id]: c.includes(opt) ? c.filter(o => o !== opt) : [...c, opt] }; }); }

  function goNext() {
    const err = validate(step, answers);
    if (err) { setError(err); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setError(null);
    if (step === 'intro')                      { setStep(0); return; }
    if (step === SECTIONS.length - 1)          { setStep('results'); return; }
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goBack() {
    setError(null);
    if (step === 'intro')    { navigate('/'); return; }
    if (step === 0)          { setStep('intro'); return; }
    if (step === 'results')  { setStep(SECTIONS.length - 1); return; }
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (step === 'results') return <ResultsPage answers={answers} ceds={ceds} matches={matches} onBack={goBack} />;

  if (step === 'intro') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: C.warm, fontFamily: SANS }}>
        <nav style={{ backgroundColor: C.navy, padding: '14px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 700, color: C.cream }}>PADD CDBG Hub</span>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: C.cream, padding: '6px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>← Back to Home</button>
        </nav>
        <div style={{ maxWidth: 620, margin: '72px auto', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>🎯</div>
          <h1 style={{ fontFamily: SERIF, fontSize: 36, fontWeight: 700, color: C.navy, margin: '0 0 16px', lineHeight: 1.2 }}>Find Funding for Your Project</h1>
          <p style={{ fontSize: 17, color: C.slate, lineHeight: 1.7, margin: '0 0 12px' }}>Tell us about your project and we will score it against PADD's CEDS criteria and match you with the funding programs you are most competitive for.</p>
          <p style={{ fontSize: 14, color: C.slate, margin: '0 0 32px' }}>Takes about <strong>5–7 minutes</strong>. The more detail you provide, the more accurate your results.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', marginBottom: 36 }}>
            {[
              '6 sections · ~22 questions · no login required',
              'Scored against PADD\'s 8-criterion, 100-point CEDS matrix',
              'Matched against 11 active funding programs',
              'Featured projects (70+) receive PADD grant-writing support',
            ].map(item => (
              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: C.slate }}>
                <span style={{ color: C.green, fontWeight: 700 }}>✓</span> {item}
              </div>
            ))}
          </div>
          <button onClick={() => setStep(0)} style={{ backgroundColor: C.gold, color: C.white, border: 'none', padding: '14px 40px', borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
            Get Started →
          </button>
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 14 }}>You do not need to have everything figured out to get useful results.</p>
        </div>
      </div>
    );
  }

  const section    = SECTIONS[step];
  const visibleQs  = section.questions.filter(q => !q.skip || !q.skip(answers));
  const progress   = ((step + 1) / SECTIONS.length) * 100;
  const isLast     = step === SECTIONS.length - 1;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: C.warm, fontFamily: SANS }}>
      <nav style={{ backgroundColor: C.navy, padding: '14px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 700, color: C.cream }}>PADD CDBG Hub</span>
        <span style={{ fontSize: 12, color: '#78909c' }}>Section {step + 1} of {SECTIONS.length}</span>
      </nav>
      <div style={{ height: 4, background: C.sand }}>
        <div style={{ height: '100%', width: `${progress}%`, background: C.gold, transition: 'width 0.3s ease' }} />
      </div>

      <div style={{ maxWidth: 660, margin: '0 auto', padding: '40px 24px 80px' }}>
        <h2 style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700, color: C.navy, margin: '0 0 4px' }}>{section.title}</h2>
        <p style={{ fontSize: 13, color: C.slate, margin: '0 0 32px' }}>Section {step + 1} of {SECTIONS.length}</p>

        {error && <div style={{ background: C.redBg, border: `1px solid ${C.redBorder}`, borderRadius: 8, padding: '12px 16px', marginBottom: 24, fontSize: 13, color: C.red, fontWeight: 600 }}>{error}</div>}

        {visibleQs.map(q => <QuestionBlock key={q.id} q={q} answers={answers} setAnswer={setAnswer} toggleCheck={toggleCheck} />)}

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <button onClick={goBack} style={{ padding: '12px 24px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', background: 'transparent', border: `1.5px solid ${C.sand}`, color: C.slate }}>← Back</button>
          <button onClick={goNext} style={{ flex: 1, padding: '12px 24px', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer', background: C.gold, border: 'none', color: C.white }}>
            {isLast ? 'See My Results →' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
}
